import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { forkJoin, Observable } from 'rxjs';

import { User } from 'src/app/model/user';
import { Submission, Review } from '../../model/paper';
import { Event } from '../../model/event';
import { EventTopic } from 'src/app/model/eventTopic';
import { AssignReview } from 'src/app/model/assign.review';

import { EventsService } from 'src/app/service/events.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { ReviewsConfigurationService } from 'src/app/service/reviews.configuration.service';
import { ReviewsService } from 'src/app/service/reviews.service';
import { AuthService } from 'src/app/service/auth.service';
import { InfoService } from 'src/app/service/info.service';
import { NotificationService } from 'src/app/service/notification.service';
import { NavbarService } from 'src/app/service/navbar.service';
import { TimezoneService } from 'src/app/service/timezone.service';
import { EventReviewConfiguration } from 'src/app/model/eventReviewConfiguration';

// Candidato = MEMBRO DO COMITÊ (só TPC pode dar bid, então a lista é o comitê),
// anotado com: deu bid nesta submissão? e quantos tópicos casam com a submissão.
export interface ReviewerCandidate {
  id: number;
  name: string;
  user: any;                       // User c/ profile (quando disponível) p/ o pipe de afiliação
  bid: boolean;                    // deu bid NESTA submissão
  reviews: number;
  claimedSubmissions: number | null;
  assignedClaimedSubmissions: number | null;
  matchedTopics: string[];         // tópicos do usuário que casam com os da submissão
  isUserReviewer: boolean;
}

// Merge das antigas fontes "Membros do comitê" + "Pesquisar por interesse" (bid
// + afinidade) numa tabela única com coluna de origem. Abre como diálogo a partir
// de "Adicionar novo revisor".
@Component({
  selector: 'app-reviewer-finder',
  templateUrl: './reviewer-finder.component.html',
  styleUrls: ['./reviewer-finder.component.scss']
})
export class ReviewerFinderComponent implements OnInit {
  public submission: Submission;
  public event: Event;
  public reviewConfiguration: EventReviewConfiguration;
  public form: FormGroup;
  public userIsChair = false;

  public candidates: ReviewerCandidate[] = [];
  public selection = new SelectionModel<ReviewerCandidate>(true, []);
  private committee: any[] = [];
  public committeeLoaded = false;

  // Default: quem deu bid no topo; empate no bid → mais tópicos casados primeiro.
  public sort = { key: 'bid', dir: -1 };
  link;

  constructor(
    private authService: AuthService,
    private navbar: NavbarService,
    private route: ActivatedRoute,
    public reviewService: ReviewsService,
    public infoService: InfoService,
    private eventsService: EventsService,
    private reviewConfigurationService: ReviewsConfigurationService,
    private submissionsService: SubmissionsService,
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private tzService: TimezoneService,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { submissionId?: number } | null,
    @Optional() public dialogRef: MatDialogRef<ReviewerFinderComponent>
  ) { }

  get selectedCount(): number { return this.selection.selected.length; }

  ngOnInit(): void {
    setTimeout(() => this.getReviewers());
  }

  getReviewers(): void {
    if (this.dialogData?.submissionId != null) {
      this.loadReviewers(this.dialogData.submissionId);
    } else {
      this.route.params.subscribe(({ id }) => this.loadReviewers(id));
    }
  }

  private loadReviewers(id): void {
    this.submissionsService.getSubmissionReviewersByInterest(id).subscribe(submission => {
      this.submission = submission;
      this.eventsService.getEventByTrack(submission.trackID).subscribe(event => {
        this.event = event;
        this.submission.event = event;
        this.link = {
          label: 'forms.buttons.assign-review-date',
          route: `/admin/${event.id}/reviews/configuration`,
          align: 'end'
        };
        // Config + isChair (permissão/prazo). Não gera candidatos — a lista é o
        // COMITÊ, que vem abaixo.
        forkJoin([
          this.reviewConfigurationService.getReviewsConfiguration(event.id),
          this.authService.directUserIsChair(event.id),
          this.authService.directUserIsAdmin()
        ]).subscribe(([rConfiguration, isChair, isAdmin]) => {
          this.reviewConfiguration = rConfiguration;
          this.initForm();
          // Admin do sistema (superuser) tem as permissões de chair em qualquer evento.
          this.userIsChair = isAdmin || isChair;
        });

        // Lista de membros do comitê (base da tabela). Cada um é anotado com bid
        // + tópicos casados a partir das listas de bid/interesse da submissão.
        this.reviewService.getReviewStatusPerReviewerListByEvent(event.id).subscribe(
          reviewerStatus => {
            this.committee = (reviewerStatus || []).filter(r => r.tpcMember);
            this.buildCandidates(this.committee);
            this.committeeLoaded = true;
          },
          () => { this.committeeLoaded = true; }
        );
      });
      this.navbar.loadingStop();
    });
  }

  // Base = membros do comitê. Cada um é enriquecido (perfil/tópicos/contagens)
  // quando aparece nas listas de bid/interesse da submissão; `bid` = está entre
  // quem deu bid NESTA submissão (só TPC pode dar bid, então bidders ⊆ comitê).
  private buildCandidates(committee: any[]): void {
    const claimsById = new Map<number, any>();
    (this.submission.userClaims || []).forEach(u => claimsById.set(u.id, u));
    const interestById = new Map<number, any>();
    (this.submission.usersByInterest || []).forEach(u => interestById.set(u.id, u));

    this.candidates = (committee || []).map(m => {
      const enrich = claimsById.get(m.id) || interestById.get(m.id) || null;
      return {
        id: m.id,
        name: m.name,
        user: enrich,
        bid: claimsById.has(m.id),
        reviews: enrich ? enrich.reviews : ((m.totalReviews || 0) - (m.declined || 0)),
        claimedSubmissions: enrich ? enrich.claimedSubmissions : null,
        assignedClaimedSubmissions: enrich ? enrich.assignedClaimedSubmissions : null,
        matchedTopics: enrich ? this.topicsFor(enrich) : [],
        isUserReviewer: this.submission.isReviewer(m.id)
      } as ReviewerCandidate;
    });
    this.sortBy(this.sort.key, this.sort.dir);
    this.selection.clear();
  }

  topicsFor(user: { topics?: EventTopic[] }): string[] {
    if (!user || !user.topics || !this.submission || !this.submission.topics) { return []; }
    return this.submission.topics
      .filter(t => user.topics.find(topic => topic.id === t.id))
      .map(t => t.name);
  }

  initForm(): void {
    this.form = this.fb.group({
      dueAt: [this.reviewConfiguration.conferenceDateDue.toDate(), [Validators.required]],
      timeZone: [this.tzService.browserTimeZone]
    });
  }

  // ── Seleção (checkbox mestre ignora quem já é revisor) ──────────────────────
  selectableCandidates(): ReviewerCandidate[] {
    return this.candidates.filter(c => !c.isUserReviewer);
  }
  isAllSelected(): boolean {
    const sel = this.selectableCandidates();
    return sel.length > 0 && sel.every(c => this.selection.isSelected(c));
  }
  masterToggle({ checked }): void {
    if (!checked) { this.selection.clear(); }
    else { this.selectableCandidates().forEach(c => this.selection.select(c)); }
  }

  today(): Date { return new Date(); }

  // ── Ordenação por coluna ────────────────────────────────────────────────────
  sortTable(key: string): void {
    const dir = (this.sort.key === key) ? -this.sort.dir : 1;
    this.sortBy(key, dir);
  }
  private sortBy(key: string, dir: number): void {
    this.sort = { key, dir };
    this.candidates = [...this.candidates].sort((a: any, b: any) => {
      // Bid?: primário pelo bid (dir). Desempates em cascata (sempre na mesma
      // direção, independem do dir): + tópicos casados → - revisões → + bids →
      // - atribuídas dos bids. (null nas contagens vira 0.)
      if (key === 'bid') {
        let cmp = ((a.bid ? 1 : 0) - (b.bid ? 1 : 0)) * dir;
        if (cmp !== 0) { return cmp; }
        cmp = b.matchedTopics.length - a.matchedTopics.length;              // + tópicos
        if (cmp !== 0) { return cmp; }
        cmp = (a.reviews || 0) - (b.reviews || 0);                          // - revisões
        if (cmp !== 0) { return cmp; }
        cmp = (b.claimedSubmissions || 0) - (a.claimedSubmissions || 0);    // + bids
        if (cmp !== 0) { return cmp; }
        return (a.assignedClaimedSubmissions || 0) - (b.assignedClaimedSubmissions || 0); // - atribuídas
      }
      let va = a[key];
      let vb = b[key];
      if (key === 'topics') { va = a.matchedTopics.length; vb = b.matchedTopics.length; }
      if (typeof va === 'number' && typeof vb === 'number') { return (va - vb) * dir; }
      return String(va == null ? '' : va).localeCompare(String(vb == null ? '' : vb), 'pt') * dir;
    });
  }
  sortIcon(key: string): string {
    if (this.sort.key !== key) { return 'unfold_more'; }
    return this.sort.dir === 1 ? 'arrow_upward' : 'arrow_downward';
  }

  addReview(): void {
    const selected = this.selection.selected;
    if (!selected.length) { return; }
    forkJoin(selected.map(c => this.saveReview(this.infoService.user, c.id, this.submission.id, this.form.value.dueAt)))
      .subscribe(reviews => {
        if (reviews.length > 1) {
          this.notificationService.notify('messages.assigned-multiple-user-reviews', { params: { users: reviews.length } });
        } else {
          this.notificationService.notify('messages.assigned-user-review', { params: { user: reviews[0].userName } });
        }
        if (this.dialogRef) { this.dialogRef.close(reviews); }
        else { this.getReviewers(); }
      }, error => {
        this.notificationService.notifyError('errors.could-not-create-review');
      });
  }

  private saveReview(assignedBy: User, userId: number, submission: number, dueAt): Observable<Review> {
    const review = new AssignReview();
    review.assignedBy = assignedBy.id;
    review.user = userId;
    review.dueAt = dueAt;
    review.submission = submission;
    return this.reviewService.createReview(review);
  }
}
